import React, { useState, useEffect, useMemo, useRef } from 'react';
import './BibleContent.css';
import localBibleData from '../data/en.json'; // The default English BBS version
import { bibleApi } from '../api';
import { storage } from '../firebase';
import { ref, getDownloadURL } from "firebase/storage";
import ChatPanel from './ChatPanel';
import GreekPanel from './GreekPanel';
import HebrewPanel from './HebrewPanel';
import AdBanner from './AdBanner';
import { buildIndex } from '../utils/bibleRag';
import { localizeBookName, HELLOAO_BOOK_CODES } from '../utils/bibleLocale';
import { loadCrossRefIndex, getCrossRefs, getVerseText } from '../utils/crossReferences';

const NT_BOOKS = new Set([
  'Matthew','Mark','Luke','John','Acts','Romans',
  '1 Corinthians','2 Corinthians','Galatians','Ephesians','Philippians',
  'Colossians','1 Thessalonians','2 Thessalonians','1 Timothy','2 Timothy',
  'Titus','Philemon','Hebrews','James','1 Peter','2 Peter',
  '1 John','2 John','3 John','Jude','Revelation',
]);

/**
 * Convert raw commentary text (from HelloAO or local translated files) into
 * an HTML string of <p> tags.
 *
 * • Translated text (Chinese etc.) already has \n\n paragraph breaks — split on those.
 * • English from HelloAO arrives as one long string with no newlines.
 *   Detect structural markers used by Matthew Henry / JFB / Clarke / Gill:
 *     Roman numeral sections  ". I. Capital"
 *     Numbered items          ". 1. Capital"
 *     Parenthetical sub-items " - (1.)"
 */
function formatCommentary(text) {
  // Normalise HTML line breaks to \n
  let t = text
    .replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n');

  if (!t.includes('\n')) {
    // Roman-numeral section headers: ". I. Cap"  ". II. Cap"  etc.
    t = t.replace(
      /([.;:])\s+(X{0,3}(?:IX|IV|VI{0,3}|I{1,3}))\.\s+(?=[A-Z])/g,
      '$1\n\n$2. '
    );
    // Numbered items: ". 1. Cap"  ": 2. Cap"
    t = t.replace(
      /([.;:])\s+(\d{1,2})\.\s+(?=[A-Z])/g,
      '$1\n\n$2. '
    );
    // Parenthetical sub-items: " - (1.)"
    t = t.replace(/\s+-\s+\((\d+)\.\)/g, '\n($1.)');
  }

  return t
    .split(/\n+/)
    .map(p => p.trim())
    .filter(p => p.length > 0)
    .map(p => `<p>${p}</p>`)
    .join('');
}

function BibleContent({
  user,
  isSubscribed,
  version,
  onDownloadCancel,
  onDownloadSuccess,
  versions,
  selectedLanguage,
  onLanguageChange,
  onVersionChange,
  targetPassage,
}) {
  const [bibleData, setBibleData] = useState(localBibleData);
  const [bookIndex, setBookIndex] = useState(0);
  const [chapterIndex, setChapterIndex] = useState(0);
  const [notes, setNotes] = useState({}); // { 'Book-Chapter-Verse': note object }
  const [activeVerse, setActiveVerse] = useState(null); // 'Book-Chapter-Verse'
  const [selectedVerse, setSelectedVerse] = useState(null); // clicked verse — shows yellow bar
  const [expandedNote, setExpandedNote] = useState(null); // 'Book-Chapter-Verse'
  const [noteInput, setNoteInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Bookmarks state
  const [bookmarks, setBookmarks] = useState({}); // { 'Book-Chapter-Verse': bookmark object }
  const [showBookmarks, setShowBookmarks] = useState(false);

  // Highlights state (localStorage only)
  const [highlights, setHighlights] = useState({}); // { 'Book-Chapter-Verse': [{ id, start, end, text, color }] }
  const [showHighlights, setShowHighlights] = useState(false);
  const [highlightToolbar, setHighlightToolbar] = useState(null); // { verseId, start, end, text, x, y }
  const [activeHighlight, setActiveHighlight] = useState(null);   // { verseId, hlId, x, y }

  const HIGHLIGHT_COLORS = [
    { id: 'yellow', color: 'rgba(255, 210, 0, 0.38)',   swatch: '#FFD700', label: 'Yellow' },
    { id: 'green',  color: 'rgba(80,  200, 100, 0.38)', swatch: '#4CAF50', label: 'Green'  },
    { id: 'blue',   color: 'rgba(80,  170, 255, 0.38)', swatch: '#2196F3', label: 'Blue'   },
    { id: 'pink',   color: 'rgba(255, 100, 160, 0.38)', swatch: '#E91E8C', label: 'Pink'   },
  ];

  // Cross-reference state
  const [crossRefIndex, setCrossRefIndex] = useState(null);
  const [expandedCrossRef, setExpandedCrossRef] = useState(null); // 'Book-Chapter-Verse'

  // Navigation history — stack of { bookIndex, chapterIndex }
  const [navHistory, setNavHistory] = useState([]);

  // Commentary state
  const [commentarySlug, setCommentarySlug] = useState('');
  const [commentaryData, setCommentaryData] = useState(null);
  const [commentaryLoading, setCommentaryLoading] = useState(false);
  const [expandedCommentary, setExpandedCommentary] = useState(null); // 'Book-Chapter-Verse'

  // Per-verse Ask AI state
  const [focusedVerseContext, setFocusedVerseContext] = useState(null); // { book, chapter, verse, verseText, commentaries }
  const [fetchingAiFor, setFetchingAiFor] = useState(null); // verse number currently being fetched
  const commentaryCacheRef = useRef({}); // { 'Book-Chapter-slug': { verseNum: text } }

  const [greekData, setGreekData] = useState(null);   // parsed TR data, loaded on demand
  const [greekLoading, setGreekLoading] = useState(false);
  const [showGreek, setShowGreek] = useState(false);
  const [hebrewData, setHebrewData] = useState(null); // parsed OSHB data, loaded on demand
  const [hebrewLoading, setHebrewLoading] = useState(false);
  const [showHebrew, setShowHebrew] = useState(false);
  const [highlightedVerse, setHighlightedVerse] = useState(null); // 1-based verse number highlighted by Greek panel
  const [highlightedGloss, setHighlightedGloss] = useState(null); // gloss terms to highlight within that verse
  const highlightedVerseRef = useRef(null);
  const importInputRef = useRef(null);
  const highlightImportInputRef = useRef(null);

  // localStorage key for guest notes, scoped to language + version
  const localNotesKey = `plutobible-local-notes-${selectedLanguage}-${version?.id || 'default'}`;
  const [bibleLoading, setBibleLoading] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [fontSize, setFontSize] = useState(18);

  const FONT_SIZES = [14, 16, 18, 20, 22, 24, 28];
  const fontSizeIdx = FONT_SIZES.indexOf(fontSize);
  const decreaseFont = () => fontSizeIdx > 0 && setFontSize(FONT_SIZES[fontSizeIdx - 1]);
  const increaseFont = () => fontSizeIdx < FONT_SIZES.length - 1 && setFontSize(FONT_SIZES[fontSizeIdx + 1]);

  const FONTS = [
    { id: 'system',         labels: { en: 'System',        zh: '系統字體',   fr: 'Système'       }, stack: 'system-ui, -apple-system, sans-serif' },
    { id: 'georgia',        labels: { en: 'Georgia',        zh: 'Georgia',    fr: 'Georgia'       }, stack: 'Georgia, serif' },
    { id: 'merriweather',   labels: { en: 'Merriweather',   zh: 'Merriweather', fr: 'Merriweather' }, stack: '"Merriweather", Georgia, serif' },
    { id: 'lora',           labels: { en: 'Lora',           zh: 'Lora',       fr: 'Lora'          }, stack: '"Lora", Georgia, serif' },
    { id: 'eb-garamond',    labels: { en: 'EB Garamond',    zh: 'EB Garamond', fr: 'EB Garamond'  }, stack: '"EB Garamond", Georgia, serif' },
    { id: 'source-serif',   labels: { en: 'Source Serif',   zh: '思源宋體',   fr: 'Source Serif'  }, stack: '"Source Serif 4", Georgia, serif' },
    { id: 'noto-serif-tc',  labels: { en: 'Noto Serif TC',  zh: '思源宋體 TC', fr: 'Noto Serif TC' }, stack: '"Noto Serif TC", serif' },
    { id: 'lxgw-wenkai-tc', labels: { en: 'LXGW WenKai TC', zh: '霞鶩文楷 TC', fr: 'LXGW WenKai TC' }, stack: '"LXGW WenKai Mono TC", serif' },
  ];
  const getFontLabel = (font) => font.labels[selectedLanguage] ?? font.labels.zh ?? font.labels.en;

  // Per-language font defaults and storage keys
  const LANG_DEFAULT_FONT = { en: 'source-serif', zh: 'noto-serif-tc', 'zh-s': 'noto-serif-tc', fr: 'source-serif' };
  const fontKey = `plutobible-font-${selectedLanguage}`;

  const COMMENTARIES = [
    { id: '',             labels: { en: 'None',          zh: '無',          fr: 'Aucun'        } },
    { id: 'matthew-henry', labels: { en: 'Matthew Henry', zh: '馬修·亨利',  fr: 'Matthew Henry' } },
    { id: 'jamieson-fausset-brown', labels: { en: 'JFB',  zh: 'JFB',        fr: 'JFB'          } },
    { id: 'adam-clarke',   labels: { en: 'Adam Clarke',   zh: '亞當·克拉克', fr: 'Adam Clarke'  } },
    { id: 'john-gill',     labels: { en: 'John Gill',     zh: '約翰·吉爾',   fr: 'John Gill'    } },
  ];

  // Localized UI strings
  const UI = {
    en: {
      chapter:         (n) => `Ch. ${n}`,
      prevChapter:     'Previous Chapter',
      nextChapter:     'Next Chapter',
      notePlaceholder: 'Add your note here...',
      save:            'Save',
      cancel:          'Cancel',
      syncingNotes:    'Syncing notes...',
      exportNotes:        'Export notes',
      importNotes:        'Import notes',
      importSuccess:      (n) => `Imported ${n} note(s).`,
      importError:        'Invalid or unreadable notes file.',
      exportHighlights:   'Export highlights',
      importHighlights:   'Import highlights',
      importHlSuccess:    (n) => `Imported ${n} highlight(s).`,
      importHlError:      'Invalid or unreadable highlights file.',
      ioLabelNotes:       'Notes',
      ioLabelHighlights:  'Highlights',
      commentary:      'Commentary',
      crossReferences: 'Cross References',
    },
    zh: {
      chapter:         (n) => `第 ${n} 章`,
      prevChapter:     '上一章',
      nextChapter:     '下一章',
      notePlaceholder: '在此添加您的筆記...',
      save:            '儲存',
      cancel:          '取消',
      syncingNotes:    '正在同步筆記...',
      exportNotes:        '匯出筆記',
      importNotes:        '匯入筆記',
      importSuccess:      (n) => `已匯入 ${n} 條筆記。`,
      importError:        '筆記檔案無效或無法讀取。',
      exportHighlights:   '匯出標記',
      importHighlights:   '匯入標記',
      importHlSuccess:    (n) => `已匯入 ${n} 條標記。`,
      importHlError:      '標記檔案無效或無法讀取。',
      ioLabelNotes:       '筆記',
      ioLabelHighlights:  '標記',
      commentary:      '解經',
      crossReferences: '相關經文',
    },
    'zh-s': {
      chapter:         (n) => `第 ${n} 章`,
      prevChapter:     '上一章',
      nextChapter:     '下一章',
      notePlaceholder: '在此添加您的笔记...',
      save:            '保存',
      cancel:          '取消',
      syncingNotes:    '正在同步笔记...',
      exportNotes:        '导出笔记',
      importNotes:        '导入笔记',
      importSuccess:      (n) => `已导入 ${n} 条笔记。`,
      importError:        '笔记文件无效或无法读取。',
      exportHighlights:   '导出标记',
      importHighlights:   '导入标记',
      importHlSuccess:    (n) => `已导入 ${n} 条标记。`,
      importHlError:      '标记文件无效或无法读取。',
      ioLabelNotes:       '笔记',
      ioLabelHighlights:  '标记',
      commentary:      '解经',
      crossReferences: '相关经文',
    },
    fr: {
      chapter:         (n) => `Chap. ${n}`,
      prevChapter:     'Chapitre précédent',
      nextChapter:     'Chapitre suivant',
      notePlaceholder: 'Ajoutez votre note ici...',
      save:            'Enregistrer',
      cancel:          'Annuler',
      syncingNotes:    'Synchronisation des notes...',
      exportNotes:        'Exporter les notes',
      importNotes:        'Importer les notes',
      importSuccess:      (n) => `${n} note(s) importée(s).`,
      importError:        'Fichier de notes invalide ou illisible.',
      exportHighlights:   'Exporter les surlignages',
      importHighlights:   'Importer les surlignages',
      importHlSuccess:    (n) => `${n} surlignage(s) importé(s).`,
      importHlError:      'Fichier de surlignages invalide ou illisible.',
      ioLabelNotes:       'Notes',
      ioLabelHighlights:  'Surlignages',
      commentary:      'Commentaire',
      crossReferences: 'Références croisées',
    },
  };
  const ui = UI[selectedLanguage] ?? UI.en;

  // Localized version names (client-side override for known IDs)
  const VERSION_NAMES = {
    'en-bbe':   { en: 'BBE',                           zh: 'BBE',             'zh-s': 'BBE',             fr: 'BBE'                           },
    'en-kjv':   { en: 'King James Version',            zh: 'KJV 英王欽定本',   'zh-s': 'KJV 英王钦定本',   fr: 'King James Version'            },
    'en-web':   { en: 'World English Bible',           zh: 'WEB 世界英文聖經', 'zh-s': 'WEB 世界英文圣经', fr: 'World English Bible'           },
    'zh-cuv':   { en: 'Chinese Union Version (Trad.)', zh: '和合本（繁體）',   'zh-s': '和合本（繁体）',   fr: 'Version Chinoise Unifiée (Trad.)' },
    'zh-cuv-s': { en: 'Chinese Union Version (Simp.)', zh: '和合本（简体）',   'zh-s': '和合本（简体）',   fr: 'Version Chinoise Unifiée (Simp.)' },
    'fr-lsg':   { en: 'Louis Segond 1910',             zh: 'LSG 路易·色貢本',  'zh-s': 'LSG 路易·赛贡本',  fr: 'Louis Segond 1910'             },
  };
  const getVersionName = (v) =>
    VERSION_NAMES[v.id]?.[selectedLanguage] ?? VERSION_NAMES[v.id]?.en ?? v.name;
  const [fontId, setFontId] = useState(
    () => localStorage.getItem(`plutobible-font-en`) || 'source-serif'
  );
  const fontStack = FONTS.find(f => f.id === fontId)?.stack || FONTS[0].stack;
  const handleFontChange = (id) => {
    setFontId(id);
    localStorage.setItem(fontKey, id);
  };

  // When language changes, restore that language's saved font (or use its default)
  useEffect(() => {
    const saved = localStorage.getItem(fontKey);
    setFontId(saved || LANG_DEFAULT_FONT[selectedLanguage] || 'source-serif');
  }, [selectedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset commentary selection on language change
  useEffect(() => {
    setCommentarySlug('');
  }, [selectedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load cross-reference index once on mount (lazy fetch + parse)
  useEffect(() => {
    loadCrossRefIndex().then(setCrossRefIndex).catch(err => console.error('Failed to load cross-reference index:', err));
  }, []);

  // Build BM25 index whenever the Bible data changes (memoized)
  const ragIndex = useMemo(() => buildIndex(bibleData), [bibleData]);

  // Scroll to — and keep visible — the verse highlighted by the Greek panel
  useEffect(() => {
    if (highlightedVerse && highlightedVerseRef.current) {
      highlightedVerseRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedVerse]);

  // Clear Greek highlight and focused verse context when navigating to a different chapter/book
  useEffect(() => {
    setHighlightedVerse(null);
    setHighlightedGloss(null);
    setFocusedVerseContext(null);
  }, [bookIndex, chapterIndex]);

  // Filter versions based on selected language
  const filteredVersions = Array.isArray(versions) 
    ? versions.filter(v => v.language === selectedLanguage)
    : [];

  // Load Bible version data
  useEffect(() => {
    const checkCacheAndLoad = async () => {
      if (version.local) {
        setBibleData(localBibleData);
        return;
      }

      // 1. Try Cache API first
      const cacheName = 'bible-versions-cache';
      const cache = await caches.open(cacheName);
      const storagePathKey = version.storage_path || version.url;
      const cachedResponse = await cache.match(storagePathKey);

      if (cachedResponse) {
        const data = await cachedResponse.json();
        setBibleData(data);
      } else {
        // Not in cache, show confirmation dialog instead of auto-downloading
        setShowDownloadDialog(true);
      }
    };

    if (version) {
      checkCacheAndLoad();
    }
  }, [version]);

  const handleDownloadConfirm = async () => {
    setShowDownloadDialog(false);
    
    try {
      setBibleLoading(true);
      const cacheName = 'bible-versions-cache';
      const cache = await caches.open(cacheName);
      const storagePathKey = version.storage_path || version.url;
      
      let downloadUrl = version.url || null;
      
      if (version.storage_path) {
        try {
          const bibleRef = ref(storage, version.storage_path);
          downloadUrl = await getDownloadURL(bibleRef);
        } catch (err) {
          console.error("Firebase storage error:", err);
          throw new Error(`Failed to find ${version.name} in storage.`);
        }
      }

      if (!downloadUrl) throw new Error("No download source found.");

      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error("Failed to download Bible version");
      
      // Store in cache
      await cache.put(storagePathKey, response.clone());
      
      const data = await response.json();
      setBibleData(data);
      if (onDownloadSuccess) onDownloadSuccess();
    } catch (error) {
      console.error("Bible download error:", error);
      alert(`Failed to load ${version.name}: ${error.message}`);
    } finally {
      setBibleLoading(false);
    }
  };

  const handleDownloadCancel = () => {
    setShowDownloadDialog(false);
    if (onDownloadCancel) onDownloadCancel();
  };

  const currentBook = bibleData[bookIndex] || bibleData[0];
  const currentChapterVerses = currentBook.chapters[chapterIndex] || currentBook.chapters[0];

  // Fetch commentary when book, chapter, or commentary slug changes
  useEffect(() => {
    const fetchCommentary = async () => {
      if (!commentarySlug) {
        setCommentaryData(null);
        return;
      }

      const englishBookName = Object.keys(HELLOAO_BOOK_CODES)[bookIndex];
      const bookCode = englishBookName ? HELLOAO_BOOK_CODES[englishBookName] : null;
      if (!bookCode) {
        console.warn(`No book code found for ${currentBook.name}`);
        return;
      }

      try {
        setCommentaryLoading(true);
        const data = await bibleApi.getCommentary(commentarySlug, bookCode, chapterIndex + 1, selectedLanguage);

        if (data?._local) {
          // Local translated format: { "Genesis 1:1": "text", ... }
          // Re-key by verse number for the UI
          const contentMap = {};
          Object.entries(data.data).forEach(([key, text]) => {
            const match = key.match(/:(\d+)$/);
            if (match) contentMap[parseInt(match[1], 10)] = text;
          });
          setCommentaryData(contentMap);
        } else if (data?.chapter?.content) {
          // HelloAO format: { chapter: { content: [{ number, content: [] }] } }
          const contentMap = {};
          data.chapter.content.forEach(item => {
            if (item.number) {
              // Join array items with double newline so the render normalizer splits them into <p> tags
              contentMap[item.number] = Array.isArray(item.content)
                ? item.content.join('\n\n')
                : item.content;
            }
          });
          setCommentaryData(contentMap);
        } else {
          setCommentaryData(null);
        }
      } catch (error) {
        console.error("Failed to fetch commentary:", error);
        setCommentaryData(null);
      } finally {
        setCommentaryLoading(false);
      }
    };

    fetchCommentary();
  }, [commentarySlug, bookIndex, chapterIndex, selectedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch notes when user, language, or version changes
  useEffect(() => {
    if (user && version?.id) {
      const fetchNotes = async () => {
        try {
          setLoading(true);
          const response = await bibleApi.getNotes(selectedLanguage, version.id);
          if (response.notes) {
            const notesMap = {};
            response.notes.forEach(n => {
              const key = `${n.book}-${n.chapter}-${n.verse}`;
              notesMap[key] = n;
            });
            setNotes(notesMap);
          }
        } catch (error) {
          console.error("Failed to fetch notes:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchNotes();
    } else {
      // Guest: load from localStorage
      try {
        const stored = localStorage.getItem(localNotesKey);
        setNotes(stored ? JSON.parse(stored) : {});
      } catch {
        setNotes({});
      }
    }
  }, [user, selectedLanguage, version?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch bookmarks when user, language, or version changes
  const localBookmarksKey = `plutobible-local-bookmarks-${selectedLanguage}-${version?.id || 'default'}`;
  const localHighlightsKey = `plutobible-highlights-${selectedLanguage}-${version?.id || 'default'}`;
  useEffect(() => {
    if (isSubscribed && user && version?.id) {
      const fetchBookmarks = async () => {
        try {
          const response = await bibleApi.getBookmarks(selectedLanguage, version.id);
          if (response.bookmarks) {
            const map = {};
            response.bookmarks.forEach(b => {
              map[`${b.book}-${b.chapter}-${b.verse}`] = b;
            });
            setBookmarks(map);
          }
        } catch (error) {
          console.error("Failed to fetch bookmarks:", error);
        }
      };
      fetchBookmarks();
    } else if (!user) {
      setBookmarks({});
    } else {
      // Logged in but not subscribed — use localStorage
      try {
        const stored = localStorage.getItem(localBookmarksKey);
        setBookmarks(stored ? JSON.parse(stored) : {});
      } catch {
        setBookmarks({});
      }
    }
  }, [isSubscribed, user, selectedLanguage, version?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load highlights from server (subscribed) or localStorage (guest/non-subscribed)
  useEffect(() => {
    if (isSubscribed && user && version?.id) {
      const fetchHighlights = async () => {
        try {
          const response = await bibleApi.getHighlights(selectedLanguage, version.id);
          if (response.highlights) {
            const map = {};
            response.highlights.forEach(h => {
              const key = `${h.book}-${h.chapter}-${h.verse}`;
              if (!map[key]) map[key] = [];
              map[key].push({ id: h.id, start: h.start_offset, end: h.end_offset, text: h.highlighted_text, color: h.color });
            });
            setHighlights(map);
          }
        } catch (error) {
          console.error("Failed to fetch highlights:", error);
        }
      };
      fetchHighlights();
    } else if (!user) {
      setHighlights({});
    } else {
      // Logged in but not subscribed — use localStorage
      try {
        const stored = localStorage.getItem(localHighlightsKey);
        setHighlights(stored ? JSON.parse(stored) : {});
      } catch {
        setHighlights({});
      }
    }
  }, [isSubscribed, user, selectedLanguage, version?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dismiss highlight toolbar when clicking outside it
  useEffect(() => {
    if (!highlightToolbar) return;
    const dismiss = (e) => {
      if (!e.target.closest('.Highlight-toolbar')) {
        setHighlightToolbar(null);
        window.getSelection()?.removeAllRanges();
      }
    };
    document.addEventListener('mousedown', dismiss);
    return () => document.removeEventListener('mousedown', dismiss);
  }, [highlightToolbar]);

  // Dismiss the remove-highlight toolbar when clicking outside it
  useEffect(() => {
    if (!activeHighlight) return;
    const dismiss = (e) => {
      if (!e.target.closest('.Highlight-remove-toolbar')) setActiveHighlight(null);
    };
    document.addEventListener('mousedown', dismiss);
    return () => document.removeEventListener('mousedown', dismiss);
  }, [activeHighlight]);

  // Push current position onto history before any navigation (capped at 50)
  const pushHistory = () => {
    setNavHistory(prev => [...prev.slice(-49), { bookIndex, chapterIndex }]);
  };

  const clearPanels = () => {
    setActiveVerse(null);
    setExpandedNote(null);
    setExpandedCommentary(null);
    setExpandedCrossRef(null);
  };

  const goBack = () => {
    setNavHistory(prev => {
      if (prev.length === 0) return prev;
      const target = prev[prev.length - 1];
      setBookIndex(target.bookIndex);
      setChapterIndex(target.chapterIndex);
      clearPanels();
      return prev.slice(0, -1);
    });
  };

  // Navigate when a reading plan passage is clicked
  useEffect(() => {
    if (!targetPassage) return;
    pushHistory();
    setBookIndex(targetPassage.bookIndex);
    setChapterIndex(targetPassage.chapterIndex);
    clearPanels();
  }, [targetPassage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBookChange = (e) => {
    pushHistory();
    setBookIndex(parseInt(e.target.value));
    setChapterIndex(0);
    clearPanels();
  };

  const handleChapterChange = (e) => {
    pushHistory();
    setChapterIndex(parseInt(e.target.value));
    clearPanels();
  };

  const goToNextChapter = () => {
    pushHistory();
    clearPanels();
    if (chapterIndex < currentBook.chapters.length - 1) {
      setChapterIndex(chapterIndex + 1);
    } else if (bookIndex < bibleData.length - 1) {
      setBookIndex(bookIndex + 1);
      setChapterIndex(0);
    }
  };

  const goToPrevChapter = () => {
    pushHistory();
    clearPanels();
    if (chapterIndex > 0) {
      setChapterIndex(chapterIndex - 1);
    } else if (bookIndex > 0) {
      const prevBookIndex = bookIndex - 1;
      setBookIndex(prevBookIndex);
      setChapterIndex(bibleData[prevBookIndex].chapters.length - 1);
    }
  };

  const makeVerseKey = (book, chapter, verse) => `${book}-${chapter}-${verse}`;

  const handleVerseClick = (verseNumber) => {
    // Don't open note editor if user just finished selecting text to highlight
    if (window.getSelection() && !window.getSelection().isCollapsed) return;
    const verseId = makeVerseKey(currentBook.name, chapterIndex + 1, verseNumber);
    if (activeVerse === verseId) {
      setActiveVerse(null);
    } else {
      setActiveVerse(verseId);
      setExpandedNote(null);
      setExpandedCommentary(null);
      setNoteInput(notes[verseId]?.content || '');
    }
  };

  const handleNoteIconClick = (e, verseNumber) => {
    e.stopPropagation();
    const verseId = makeVerseKey(currentBook.name, chapterIndex + 1, verseNumber);
    if (activeVerse === verseId) {
      setActiveVerse(null);
    } else {
      setActiveVerse(verseId);
      setExpandedCommentary(null);
      setExpandedCrossRef(null);
      setNoteInput(notes[verseId]?.content || '');
    }
  };

  const handleCommentaryIconClick = (e, verseNumber) => {
    e.stopPropagation();
    const verseId = makeVerseKey(currentBook.name, chapterIndex + 1, verseNumber);
    setExpandedCommentary(expandedCommentary === verseId ? null : verseId);
    setExpandedNote(null);
  };

  const handleCrossRefIconClick = (e, verseNumber) => {
    e.stopPropagation();
    const verseId = makeVerseKey(currentBook.name, chapterIndex + 1, verseNumber);
    setExpandedCrossRef(expandedCrossRef === verseId ? null : verseId);
    setExpandedNote(null);
    setExpandedCommentary(null);
  };

  const handleBookmarkClick = async (e, verseNumber) => {
    e.stopPropagation();
    const verseId = makeVerseKey(currentBook.name, chapterIndex + 1, verseNumber);
    const existing = bookmarks[verseId];

    if (existing) {
      // Optimistic remove
      setBookmarks(prev => { const n = { ...prev }; delete n[verseId]; return n; });
      if (isSubscribed) {
        try {
          await bibleApi.deleteBookmark(existing.id);
        } catch (error) {
          console.error("Failed to delete bookmark:", error);
          setBookmarks(prev => ({ ...prev, [verseId]: existing })); // revert
        }
      } else {
        const next = { ...bookmarks };
        delete next[verseId];
        localStorage.setItem(localBookmarksKey, JSON.stringify(next));
      }
    } else {
      const bookmarkData = {
        book: currentBook.name,
        chapter: chapterIndex + 1,
        verse: verseNumber,
        language: selectedLanguage,
        version_id: version?.id,
      };
      const tempBm = { ...bookmarkData, id: `local-${Date.now()}`, created_at: new Date().toISOString() };
      // Optimistic add
      setBookmarks(prev => ({ ...prev, [verseId]: tempBm }));
      if (isSubscribed) {
        try {
          const response = await bibleApi.createBookmark(bookmarkData);
          if (response.bookmark) {
            setBookmarks(prev => ({ ...prev, [verseId]: response.bookmark }));
          }
        } catch (error) {
          console.error("Failed to create bookmark:", error);
          setBookmarks(prev => { const n = { ...prev }; delete n[verseId]; return n; }); // revert
        }
      } else {
        const next = { ...bookmarks, [verseId]: tempBm };
        localStorage.setItem(localBookmarksKey, JSON.stringify(next));
      }
    }
  }; // eslint-disable-line react-hooks/exhaustive-deps

  const removeBookmark = async (e, bm) => {
    e.stopPropagation();
    const verseId = `${bm.book}-${bm.chapter}-${bm.verse}`;
    // Optimistic remove
    setBookmarks(prev => { const n = { ...prev }; delete n[verseId]; return n; });
    if (isSubscribed) {
      try {
        await bibleApi.deleteBookmark(bm.id);
      } catch (error) {
        console.error("Failed to delete bookmark:", error);
        setBookmarks(prev => ({ ...prev, [verseId]: bm })); // revert
      }
    } else {
      const next = { ...bookmarks };
      delete next[verseId];
      localStorage.setItem(localBookmarksKey, JSON.stringify(next));
    }
  };

  const navigateToBookmark = (bookmark) => {
    const bookIdx = bibleData.findIndex(b => b.name === bookmark.book);
    if (bookIdx === -1) return;
    pushHistory();
    setBookIndex(bookIdx);
    setChapterIndex(bookmark.chapter - 1);
    clearPanels();
    setShowBookmarks(false);
  };

  const navigateToCrossRef = (book, chapter) => {
    const bookIdx = bibleData.findIndex(b => b.name === book);
    if (bookIdx === -1) return;
    pushHistory();
    setBookIndex(bookIdx);
    setChapterIndex(chapter - 1);
    clearPanels();
  };

  // ── Highlight handlers ───────────────────────────────────────────────────

  const handleVerseMouseUp = (e, verseNumber) => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!range.toString().trim()) return;

    const verseBlock = e.currentTarget;
    if (!verseBlock.contains(range.commonAncestorContainer)) return;

    // Ignore selections that land entirely inside the verse number
    const numSpan = verseBlock.querySelector('.Verse-num');
    if (numSpan && numSpan.contains(range.commonAncestorContainer)) return;

    const verseId = makeVerseKey(currentBook.name, chapterIndex + 1, verseNumber);

    // Compute char offsets relative to verse text (i.e. everything after .Verse-num).
    // preRange spans from the first node after .Verse-num to the selection start.
    const preRange = range.cloneRange();
    preRange.selectNodeContents(verseBlock);
    if (numSpan && numSpan.nextSibling) {
      preRange.setStart(numSpan.nextSibling, 0);
    }
    preRange.setEnd(range.startContainer, range.startOffset);
    const start = preRange.toString().length;
    const end = start + range.toString().length;

    const rect = range.getBoundingClientRect();
    setHighlightToolbar({ verseId, start, end, text: range.toString(), x: rect.left + rect.width / 2, y: rect.top });
  };

  const applyHighlight = async (color) => {
    if (!highlightToolbar) return;
    const { verseId, start, end, text } = highlightToolbar;
    const parts = verseId.split('-');
    const verse = parseInt(parts[parts.length - 1]);
    const chapter = parseInt(parts[parts.length - 2]);
    const book = parts.slice(0, -2).join('-');

    const tempId = `hl-${Date.now()}`;
    const newHl = { id: tempId, start, end, color, text };

    // Optimistic add
    setHighlights(prev => {
      const existing = prev[verseId] || [];
      if (existing.some(hl => !(end <= hl.start || start >= hl.end))) return prev;
      const next = { ...prev, [verseId]: [...existing, newHl] };
      if (!isSubscribed) localStorage.setItem(localHighlightsKey, JSON.stringify(next));
      return next;
    });
    setHighlightToolbar(null);
    window.getSelection()?.removeAllRanges();

    if (isSubscribed) {
      try {
        const response = await bibleApi.createHighlight({
          book, chapter, verse,
          start_offset: start, end_offset: end,
          highlighted_text: text, color,
          language: selectedLanguage, version_id: version?.id,
        });
        if (response.highlight) {
          // Replace temp id with real server id
          setHighlights(prev => {
            const verseHls = (prev[verseId] || []).map(hl =>
              hl.id === tempId ? { ...hl, id: response.highlight.id } : hl
            );
            return { ...prev, [verseId]: verseHls };
          });
        }
      } catch (error) {
        console.error("Failed to save highlight:", error);
        // Revert on failure
        setHighlights(prev => {
          const filtered = (prev[verseId] || []).filter(hl => hl.id !== tempId);
          const next = { ...prev };
          if (filtered.length === 0) delete next[verseId]; else next[verseId] = filtered;
          return next;
        });
      }
    }
  };

  const removeHighlight = async (verseId, hlId) => {
    const existing = highlights[verseId]?.find(hl => hl.id === hlId);

    // Optimistic remove
    setHighlights(prev => {
      const filtered = (prev[verseId] || []).filter(hl => hl.id !== hlId);
      const next = { ...prev };
      if (filtered.length === 0) delete next[verseId]; else next[verseId] = filtered;
      if (!isSubscribed) localStorage.setItem(localHighlightsKey, JSON.stringify(next));
      return next;
    });

    if (isSubscribed && existing && typeof existing.id === 'number') {
      try {
        await bibleApi.deleteHighlight(existing.id);
      } catch (error) {
        console.error("Failed to delete highlight:", error);
        // Revert on failure
        if (existing) {
          setHighlights(prev => ({
            ...prev,
            [verseId]: [...(prev[verseId] || []), existing],
          }));
        }
      }
    }
  };

  const navigateToHighlight = (verseId) => {
    const parts = verseId.split('-');
    const chapter = parseInt(parts[parts.length - 2]);
    const book = parts.slice(0, -2).join('-');
    const bookIdx = bibleData.findIndex(b => b.name === book);
    if (bookIdx === -1) return;
    pushHistory();
    setBookIndex(bookIdx);
    setChapterIndex(chapter - 1);
    clearPanels();
    setShowHighlights(false);
  };

  // Renders verse text with saved highlight spans overlaid.
  // Returns a plain string (no highlights) or an array of strings + <mark> elements.
  // No outer wrapper — callers render this directly so font-size inheritance is unaffected.
  const renderVerseText = (verseText, verseId) => {
    const verseHls = highlights[verseId];
    if (!verseHls || verseHls.length === 0) return verseText;
    const sorted = [...verseHls].sort((a, b) => a.start - b.start);
    const parts = [];
    let cursor = 0;
    for (const hl of sorted) {
      const s = Math.max(hl.start, cursor);
      if (s >= hl.end || s >= verseText.length) continue;
      if (s > cursor) parts.push(verseText.slice(cursor, s));
      parts.push(
        <mark
          key={hl.id}
          className={`Verse-highlight${activeHighlight?.hlId === hl.id ? ' is-active' : ''}`}
          style={{ backgroundColor: hl.color }}
          onClick={(e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            setActiveHighlight({ verseId, hlId: hl.id, x: rect.left + rect.width / 2, y: rect.top });
          }}
        >
          {verseText.slice(s, Math.min(hl.end, verseText.length))}
        </mark>
      );
      cursor = hl.end;
    }
    if (cursor < verseText.length) parts.push(verseText.slice(cursor));
    return parts;
  };

  const chapterPrefix = `${currentBook.name}-${chapterIndex + 1}-`;
  const chapterHighlights = Object.entries(highlights)
    .filter(([k]) => k.startsWith(chapterPrefix))
    .reduce((sum, [, hls]) => sum + hls.length, 0);
  const chapterBookmarks = Object.keys(bookmarks).filter(k => k.startsWith(chapterPrefix)).length;

  const handleExportNotes = () => {
    // Collect all local notes across every language/version from localStorage
    const allNotes = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key.startsWith('plutobible-local-notes-')) continue;
      try {
        const notesMap = JSON.parse(localStorage.getItem(key));
        // key format: plutobible-local-notes-{lang}-{version_id}
        const suffix = key.slice('plutobible-local-notes-'.length); // e.g. "en-en-kjv"
        const dashIdx = suffix.indexOf('-');
        const lang = suffix.slice(0, dashIdx);
        const versionId = suffix.slice(dashIdx + 1);
        Object.values(notesMap).forEach(note => {
          allNotes.push({ language: lang, version_id: versionId, ...note });
        });
      } catch { /* skip malformed entries */ }
    }

    // Also include any server notes currently loaded (logged-in users)
    if (user && Object.keys(notes).length > 0) {
      Object.values(notes).forEach(note => {
        const alreadyLocal = allNotes.some(
          n => n.book === note.book && n.chapter === note.chapter && n.verse === note.verse
            && n.language === selectedLanguage && n.version_id === version?.id
        );
        if (!alreadyLocal) {
          allNotes.push({
            language: selectedLanguage,
            version_id: version?.id,
            book: note.book,
            chapter: note.chapter,
            verse: note.verse,
            content: note.content,
            updated_at: note.updated_at,
          });
        }
      });
    }

    const exportData = {
      version: 1,
      exported_at: new Date().toISOString(),
      app: 'PlutoBible',
      notes: allNotes,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plutobible-notes-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportNotes = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (data.version !== 1 || !Array.isArray(data.notes)) {
          alert(ui.importError);
          return;
        }

        // Group by localStorage key and merge (imported notes win on conflict)
        const grouped = {};
        data.notes.forEach(note => {
          const key = `plutobible-local-notes-${note.language}-${note.version_id}`;
          if (!grouped[key]) grouped[key] = {};
          const verseKey = `${note.book}-${note.chapter}-${note.verse}`;
          grouped[key][verseKey] = {
            content: note.content,
            updated_at: note.updated_at,
            book: note.book,
            chapter: note.chapter,
            verse: note.verse,
          };
        });

        // Write every group to localStorage
        Object.entries(grouped).forEach(([key, incoming]) => {
          try {
            const existing = JSON.parse(localStorage.getItem(key) || '{}');
            localStorage.setItem(key, JSON.stringify({ ...existing, ...incoming }));
          } catch { /* skip */ }
        });

        // Immediately reflect notes for the current language/version in UI state
        // so already-expanded notes update without requiring a toggle.
        const relevantKey = `plutobible-local-notes-${selectedLanguage}-${version?.id || 'default'}`;
        if (grouped[relevantKey]) {
          setNotes(prev => ({ ...prev, ...grouped[relevantKey] }));
        }


        alert(ui.importSuccess(data.notes.length));
      } catch {
        alert(ui.importError);
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // allow re-importing same file
  };

  // Export highlights — fields match backend UserHighlight schema
  const handleExportHighlights = () => {
    const allHighlights = [];

    // Collect from localStorage (guest or all versions)
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key.startsWith('plutobible-highlights-')) continue;
      try {
        const map = JSON.parse(localStorage.getItem(key));
        // key format: plutobible-highlights-{lang}-{version_id}
        const suffix = key.slice('plutobible-highlights-'.length);
        const dashIdx = suffix.indexOf('-');
        const lang = suffix.slice(0, dashIdx);
        const versionId = suffix.slice(dashIdx + 1);
        Object.entries(map).forEach(([verseKey, hls]) => {
          const parts = verseKey.split('-');
          const verse = parseInt(parts[parts.length - 1]);
          const chapter = parseInt(parts[parts.length - 2]);
          const book = parts.slice(0, -2).join('-');
          hls.forEach(hl => {
            allHighlights.push({
              language: lang,
              version_id: versionId,
              book, chapter, verse,
              start_offset: hl.start,
              end_offset: hl.end,
              highlighted_text: hl.text,
              color: hl.color,
            });
          });
        });
      } catch { /* skip malformed */ }
    }

    // Also include server highlights currently loaded (logged-in users)
    if (user && Object.keys(highlights).length > 0) {
      Object.entries(highlights).forEach(([verseKey, hls]) => {
        const parts = verseKey.split('-');
        const verse = parseInt(parts[parts.length - 1]);
        const chapter = parseInt(parts[parts.length - 2]);
        const book = parts.slice(0, -2).join('-');
        hls.forEach(hl => {
          const alreadyLocal = allHighlights.some(
            h => h.book === book && h.chapter === chapter && h.verse === verse
              && h.start_offset === hl.start && h.language === selectedLanguage
              && h.version_id === version?.id
          );
          if (!alreadyLocal) {
            allHighlights.push({
              language: selectedLanguage,
              version_id: version?.id,
              book, chapter, verse,
              start_offset: hl.start,
              end_offset: hl.end,
              highlighted_text: hl.text,
              color: hl.color,
            });
          }
        });
      });
    }

    const exportData = {
      version: 1,
      exported_at: new Date().toISOString(),
      app: 'PlutoBible',
      highlights: allHighlights,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plutobible-highlights-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import highlights — fields match backend UserHighlight schema
  const handleImportHighlights = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (data.version !== 1 || !Array.isArray(data.highlights)) {
          alert(ui.importHlError);
          return;
        }

        // Group by localStorage key and merge (imported wins on conflict)
        const grouped = {};
        data.highlights.forEach(hl => {
          const key = `plutobible-highlights-${hl.language}-${hl.version_id}`;
          if (!grouped[key]) grouped[key] = {};
          const verseKey = `${hl.book}-${hl.chapter}-${hl.verse}`;
          if (!grouped[key][verseKey]) grouped[key][verseKey] = [];
          grouped[key][verseKey].push({
            id: `hl-import-${Date.now()}-${Math.random()}`,
            start: hl.start_offset,
            end: hl.end_offset,
            text: hl.highlighted_text,
            color: hl.color,
          });
        });

        Object.entries(grouped).forEach(([key, incoming]) => {
          try {
            const existing = JSON.parse(localStorage.getItem(key) || '{}');
            // Merge verse arrays (append, avoid duplicates by offset)
            const merged = { ...existing };
            Object.entries(incoming).forEach(([verseKey, newHls]) => {
              const cur = merged[verseKey] || [];
              newHls.forEach(hl => {
                if (!cur.some(h => h.start === hl.start && h.end === hl.end)) {
                  cur.push(hl);
                }
              });
              merged[verseKey] = cur;
            });
            localStorage.setItem(key, JSON.stringify(merged));
          } catch { /* skip */ }
        });

        // Reflect imported highlights in current view
        const relevantKey = `plutobible-highlights-${selectedLanguage}-${version?.id || 'default'}`;
        if (grouped[relevantKey]) {
          setHighlights(prev => {
            const merged = { ...prev };
            Object.entries(grouped[relevantKey]).forEach(([verseKey, newHls]) => {
              const cur = merged[verseKey] || [];
              newHls.forEach(hl => {
                if (!cur.some(h => h.start === hl.start && h.end === hl.end)) cur.push(hl);
              });
              merged[verseKey] = cur;
            });
            return merged;
          });
        }

        alert(ui.importHlSuccess(data.highlights.length));
      } catch {
        alert(ui.importHlError);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleToggleGreek = async () => {
    // Close chat if open
    if (showGreek) { setShowGreek(false); return; }

    // Load Greek data on first use, then cache in state
    if (!greekData) {
      try {
        setGreekLoading(true);
        const cacheName = 'bible-versions-cache';
        const cache     = await caches.open(cacheName);
        const cacheKey  = 'bibles/grc-trparsed.json';
        const cached    = await cache.match(cacheKey);

        let data;
        if (cached) {
          data = await cached.json();
        } else {
          const storageRef  = ref(storage, cacheKey);
          const downloadUrl = await getDownloadURL(storageRef);
          const response    = await fetch(downloadUrl);
          if (!response.ok) throw new Error('Failed to download Greek NT data');
          await cache.put(cacheKey, response.clone());
          data = await response.json();
        }
        setGreekData(data);
      } catch (err) {
        console.error('Failed to load Greek data:', err);
        alert('Could not load Greek NT data. Please check your connection.');
        return;
      } finally {
        setGreekLoading(false);
      }
    }
    setShowGreek(true);
  };

  const handleToggleHebrew = async () => {
    if (showHebrew) { setShowHebrew(false); return; }

    if (!hebrewData) {
      try {
        setHebrewLoading(true);
        const cacheName = 'bible-versions-cache';
        const cache     = await caches.open(cacheName);
        const cacheKey  = 'bibles/heb-oshb.json';
        const cached    = await cache.match(cacheKey);

        let data;
        if (cached) {
          data = await cached.json();
        } else {
          const storageRef  = ref(storage, cacheKey);
          const downloadUrl = await getDownloadURL(storageRef);
          const response    = await fetch(downloadUrl);
          if (!response.ok) throw new Error('Failed to download Hebrew OT data');
          await cache.put(cacheKey, response.clone());
          data = await response.json();
        }
        setHebrewData(data);
      } catch (err) {
        console.error('Failed to load Hebrew data:', err);
        alert('Could not load Hebrew OT data. Please check your connection.');
        return;
      } finally {
        setHebrewLoading(false);
      }
    }
    setShowHebrew(true);
  };

  // Parse a commentary API response into { verseNum: text } — shared by display and AI fetch
  function parseCommentaryData(data) {
    if (!data) return {};
    if (data._local) {
      const map = {};
      Object.entries(data.data).forEach(([key, text]) => {
        const m = key.match(/:(\d+)$/);
        if (m) map[parseInt(m[1], 10)] = text;
      });
      return map;
    }
    if (data?.chapter?.content) {
      const map = {};
      data.chapter.content.forEach(item => {
        if (item.number) {
          map[item.number] = Array.isArray(item.content)
            ? item.content.join('\n\n') : item.content;
        }
      });
      return map;
    }
    return {};
  }

  const ALL_COMMENTARY_SLUGS = ['matthew-henry', 'jamieson-fausset-brown', 'adam-clarke', 'john-gill'];

  const handleAskAIForVerse = async (e, verseNumber) => {
    e.stopPropagation();
    if (fetchingAiFor !== null) return;

    setFetchingAiFor(verseNumber);
    const englishBookName = Object.keys(HELLOAO_BOOK_CODES)[bookIndex];
    const bookCode = englishBookName ? HELLOAO_BOOK_CODES[englishBookName] : null;
    const chapter = chapterIndex + 1;
    const verseText = currentChapterVerses[verseNumber - 1];

    const commentaryTexts = {};
    await Promise.all(ALL_COMMENTARY_SLUGS.map(async (slug) => {
      const cacheKey = `${currentBook.name}-${chapter}-${slug}-${selectedLanguage}`;
      let map = commentaryCacheRef.current[cacheKey];
      if (!map) {
        try {
          const data = await bibleApi.getCommentary(slug, bookCode, chapter, selectedLanguage);
          map = parseCommentaryData(data);
          commentaryCacheRef.current[cacheKey] = map;
        } catch {
          map = {};
        }
      }
      if (map[verseNumber]) commentaryTexts[slug] = map[verseNumber];
    }));

    setFocusedVerseContext({ book: currentBook.name, chapter, verse: verseNumber, verseText, commentaries: commentaryTexts });
    setFetchingAiFor(null);
    setShowChat(true);
  };

  const saveNote = async (verseNumber, target = 'remote') => {
    const verseKey = makeVerseKey(currentBook.name, chapterIndex + 1, verseNumber);
    const trimmedContent = noteInput.trim();

    if (user && target === 'remote') {
      // Logged-in user chose to save to the cloud
      try {
        if (trimmedContent) {
          const response = await bibleApi.createNote({
            book: currentBook.name,
            chapter: chapterIndex + 1,
            verse: verseNumber,
            content: trimmedContent,
            language: selectedLanguage,
            version_id: version?.id
          });
          if (response.note) {
            setNotes({ ...notes, [verseKey]: response.note });
          }
        } else {
          if (notes[verseKey]?.id) {
            await bibleApi.deleteNote(notes[verseKey].id);
          }
          const newNotes = { ...notes };
          delete newNotes[verseKey];
          setNotes(newNotes);
        }
      } catch (error) {
        console.error("Failed to save note to server:", error);
        alert("Failed to save note to server. Please try again.");
      }
    } else {
      // Guest or logged-in user chose local save — persist to localStorage only
      const newNotes = { ...notes };
      if (trimmedContent) {
        newNotes[verseKey] = {
          content: trimmedContent,
          updated_at: new Date().toISOString(),
          book: currentBook.name,
          chapter: chapterIndex + 1,
          verse: verseNumber,
        };
      } else {
        delete newNotes[verseKey];
      }
      setNotes(newNotes);
      localStorage.setItem(localNotesKey, JSON.stringify(newNotes));
    }

    setActiveVerse(null);
  };

  // Wrap gloss-matching words in <mark> spans for Greek word highlighting
  const highlightWords = (text, terms) => {
    if (!terms || terms.length === 0) return text;
    const escaped = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(`\\b(${escaped.join('|')})\\b`, 'gi');
    const parts = [];
    let last = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
      if (match.index > last) parts.push(text.slice(last, match.index));
      parts.push(<mark key={match.index} className="Greek-word-match">{match[0]}</mark>);
      last = match.index + match[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts.length > 1 ? parts : text;
  };

  const isNTBook = NT_BOOKS.has(currentBook.name);
  const isOTBook = !isNTBook;

  const greekBook        = greekData?.find(b => b.name === currentBook.name);
  const greekChapterData = greekBook?.chapters[chapterIndex] || null;

  const hebrewBook        = hebrewData?.find(b => b.name === currentBook.name);
  const hebrewChapterData = hebrewBook?.chapters[chapterIndex] || null;

  if (bibleLoading) {
    return <div className="Bible-loading-screen">Downloading {version.name}...</div>;
  }

  return (
    <div className={`Bible-content-container ${showChat ? 'with-chat' : ''} ${showGreek ? 'with-greek' : ''} ${showHebrew ? 'with-hebrew' : ''}`}>
      {showDownloadDialog && (
        <div className="Download-dialog-overlay">
          <div className="Download-dialog">
            <div className="Download-dialog-icon">📖</div>
            <h3>Download Bible Version</h3>
            <div className="Download-version-badge">{version.name}</div>
            <p>
              This version ({version.size || 'approx. 5MB'}) hasn't been downloaded to your device yet.
              Once downloaded, it will load instantly — even offline.
            </p>
            <button className="Download-btn confirm" onClick={handleDownloadConfirm}>
              ↓ Download Now
            </button>
            <button className="Download-btn cancel" onClick={handleDownloadCancel}>
              Maybe later
            </button>
          </div>
        </div>
      )}

      {loading && <div className="loading-overlay">{ui.syncingNotes}</div>}

      {showBookmarks && (
        <div className="Bookmarks-panel">
          <div className="Bookmarks-panel-header">
            <span className="Bookmarks-panel-title">🔖 Bookmarks</span>
            <button className="Bookmarks-close" onClick={() => setShowBookmarks(false)} aria-label="Close bookmarks">✕</button>
          </div>
          <div className="Bookmarks-list">
            {Object.keys(bookmarks).length === 0 ? (
              <div className="Bookmarks-empty">No bookmarks yet.<br/>Click a verse, then tap 🔖 to save it.</div>
            ) : (
              Object.values(bookmarks)
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map(bm => {
                  const verseText = getVerseText(bibleData, bm.book, bm.chapter, bm.verse);
                  const bmKey = `${bm.book}-${bm.chapter}-${bm.verse}`;
                  return (
                    <div key={bmKey} className="Bookmark-item">
                      <div className="Bookmark-item-body" onClick={() => navigateToBookmark(bm)}>
                        <div className="Bookmark-ref">{bm.book} {bm.chapter}:{bm.verse}</div>
                        {verseText && <div className="Bookmark-preview">{verseText.length > 90 ? verseText.slice(0, 90) + '…' : verseText}</div>}
                      </div>
                      <button
                        className="Bookmark-remove"
                        onClick={(e) => removeBookmark(e, bm)}
                        title="Remove bookmark"
                        aria-label="Remove bookmark"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })
            )}
          </div>
        </div>
      )}

      {showHighlights && (
        <div className="Highlights-panel">
          <div className="Highlights-panel-header">
            <span className="Highlights-panel-title">🖍 Highlights</span>
            <button className="Highlights-close" onClick={() => setShowHighlights(false)} aria-label="Close highlights">✕</button>
          </div>
          <div className="Highlights-list">
            {Object.keys(highlights).length === 0 ? (
              <div className="Highlights-empty">No highlights yet.<br/>Select text in any verse to highlight it.</div>
            ) : (
              Object.entries(highlights)
                .sort(([aKey], [bKey]) => {
                  const ap = aKey.split('-'), bp = bKey.split('-');
                  const aBook = ap.slice(0, -2).join('-'), bBook = bp.slice(0, -2).join('-');
                  const ai = bibleData.findIndex(b => b.name === aBook);
                  const bi = bibleData.findIndex(b => b.name === bBook);
                  if (ai !== bi) return ai - bi;
                  const aCh = parseInt(ap[ap.length - 2]), bCh = parseInt(bp[bp.length - 2]);
                  if (aCh !== bCh) return aCh - bCh;
                  return parseInt(ap[ap.length - 1]) - parseInt(bp[bp.length - 1]);
                })
                .flatMap(([verseId, verseHls]) => {
                  const parts = verseId.split('-');
                  const verse = parseInt(parts[parts.length - 1]);
                  const chapter = parseInt(parts[parts.length - 2]);
                  const book = parts.slice(0, -2).join('-');
                  return verseHls.map(hl => (
                    <div key={hl.id} className="Highlight-item">
                      <div className="Highlight-item-body" onClick={() => navigateToHighlight(verseId)}>
                        <div className="Highlight-ref">{book} {chapter}:{verse}</div>
                        <span className="Highlight-text" style={{ backgroundColor: hl.color }}>{hl.text}</span>
                      </div>
                      <button
                        className="Highlight-remove"
                        onClick={() => removeHighlight(verseId, hl.id)}
                        title="Remove highlight"
                        aria-label="Remove highlight"
                      >✕</button>
                    </div>
                  ));
                })
            )}
          </div>
        </div>
      )}

      <div className="Bible-text-column">
      <div className="Bible-header-info">
        <div className="Bible-selectors">
          <div className="Selector-item">
            <span className="Selector-label">Lang</span>
            <select
              className="Bible-dropdown"
              value={selectedLanguage}
              onChange={onLanguageChange}
              aria-label="Language Selector"
            >
              <option value="en">English</option>
              <option value="zh">繁體中文</option>
              <option value="zh-s">简体中文</option>
              <option value="fr">Français</option>
            </select>
          </div>

          <div className="Selector-item">
            <span className="Selector-label">Version</span>
            <select
              className="Bible-dropdown"
              value={version?.id || ''}
              onChange={onVersionChange}
              aria-label="Bible Version Selector"
            >
              {filteredVersions.map(v => (
                <option key={v.id} value={v.id}>{getVersionName(v)}</option>
              ))}
            </select>
          </div>

          <div className="Selector-item">
            <span className="Selector-label">Book</span>
            <select
              className="Bible-dropdown"
              value={bookIndex}
              onChange={handleBookChange}
              aria-label="Select Book"
            >
              {bibleData.map((book, index) => (
                <option key={book.id || index} value={index}>{localizeBookName(book.name, selectedLanguage)}</option>
              ))}
            </select>
          </div>

          <div className="Selector-item">
            <span className="Selector-label">Chapter</span>
            <select
              className="Bible-dropdown"
              value={chapterIndex}
              onChange={handleChapterChange}
              aria-label="Select Chapter"
            >
              {currentBook.chapters.map((_, index) => (
                <option key={index} value={index}>{ui.chapter(index + 1)}</option>
              ))}
            </select>
          </div>

          <div className="Selector-item">
            <span className="Selector-label">{ui.commentary}</span>
            <select
              className="Bible-dropdown"
              value={commentarySlug}
              onChange={e => setCommentarySlug(e.target.value)}
              aria-label={ui.commentary}
            >
              {COMMENTARIES.map(c => (
                <option key={c.id} value={c.id}>{c.labels[selectedLanguage] || c.labels[selectedLanguage.split('-')[0]] || c.labels.en}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="Bible-navigation-controls">
          {navHistory.length > 0 && (() => {
            const t = navHistory[navHistory.length - 1];
            const label = `${bibleData[t.bookIndex]?.name} ${t.chapterIndex + 1}`;
            return (
              <button
                className="Nav-btn back-btn"
                onClick={goBack}
                aria-label={`Go back to ${label}`}
                title={`Back to ${label}`}
              >
                <svg viewBox="0 0 300 300" width="20" height="20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M126.806,18.122c-3.3-4.4-9.2-5.8-14.1-3.3l-96.9,49.8c-3.8,1.9-6.2,5.8-6.2,10.1s2.4,8.2,6.2,10.1l96.9,49.8
                    c4.9,2.5,10.8,1.1,14.1-3.3c3.3-4.5,3-10.6-0.8-14.7l-28.3-29.9c19.9,1.4,55.1,7.9,83.4,32c33.1,28.2,49.9,73.7,49.9,135.2
                    c0,6.5,4.5,12.1,10.9,13.4c0.9,0.2,1.8,0.3,2.7,0.3c5.4,0,10.4-3.3,12.5-8.5c2.4-5.9,57-145.5-111.2-182.1l19.5-20.6
                    C129.806,28.722,130.106,22.622,126.806,18.122z"/>
                </svg>
              </button>
            );
          })()}
          <button
            className="Nav-btn"
            onClick={goToPrevChapter}
            aria-label="Previous Chapter"
            disabled={bookIndex === 0 && chapterIndex === 0}
          >
            <svg viewBox="0 0 72 72" width="28" height="28" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M40.016,59.212L20.791,39.984c-2.2-2.2-2.2-5.767,0-7.967l19.225-19.229c2.2-2.201,5.768-2.201,7.968,0l3.224,3.224
                c2.2,2.2,2.2,5.768,0,7.968L39.188,36l12.02,12.02c2.2,2.2,2.2,5.768,0,7.968l-3.224,3.224
                C45.784,61.413,42.217,61.413,40.016,59.212z"/>
            </svg>
          </button>
          <button
            className="Nav-btn"
            onClick={goToNextChapter}
            aria-label="Next Chapter"
            disabled={bookIndex === bibleData.length - 1 && chapterIndex === currentBook.chapters.length - 1}
          >
            <svg viewBox="0 0 64 64" width="28" height="28" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M21.364,52.6l-2.861-2.861c-1.953-1.953-1.953-5.118,0-7.071L29.17,32L18.502,21.332c-1.953-1.953-1.953-5.118,0-7.071
                l2.861-2.861c1.953-1.953,5.119-1.953,7.071,0l17.062,17.064c1.952,1.953,1.952,5.118,0,7.07L28.435,52.6
                C26.482,54.553,23.316,54.553,21.364,52.6z"/>
            </svg>
          </button>

          <div className="Font-size-control" title="Adjust font size">
            <button className="Font-size-btn" onClick={decreaseFont} disabled={fontSizeIdx === 0} aria-label="Decrease font size">A−</button>
            <span className="Font-size-label">{fontSize}px</span>
            <button className="Font-size-btn" onClick={increaseFont} disabled={fontSizeIdx === FONT_SIZES.length - 1} aria-label="Increase font size">A+</button>
          </div>

          <div className="Font-family-control" title="Choose font style">
            <select
              className="Font-family-select"
              value={fontId}
              onChange={e => handleFontChange(e.target.value)}
              aria-label="Font style"
              style={{ fontFamily: fontStack }}
            >
              {FONTS.map(f => (
                <option key={f.id} value={f.id} style={{ fontFamily: f.stack }}>{getFontLabel(f)}</option>
              ))}
            </select>
          </div>

          {isNTBook && (
            <button
              className={`Greek-toggle-btn${showGreek ? ' active' : ''}`}
              onClick={handleToggleGreek}
              disabled={greekLoading}
              title="View original Greek text (Textus Receptus)"
              aria-label="Toggle Greek reader"
            >
              {greekLoading ? '…' : 'α'}
            </button>
          )}

          {isOTBook && (
            <button
              className={`Hebrew-toggle-btn${showHebrew ? ' active' : ''}`}
              onClick={handleToggleHebrew}
              disabled={hebrewLoading}
              title="View original Hebrew text (OSHB)"
              aria-label="Toggle Hebrew reader"
            >
              {hebrewLoading ? '…' : 'א'}
            </button>
          )}

          <button
            className={`Bookmark-nav-btn${showBookmarks ? ' active' : ''}${chapterBookmarks > 0 ? ' has-bookmarks' : ''}`}
            onClick={() => setShowBookmarks(v => !v)}
            title="Bookmarks"
            aria-label="Bookmarks"
          >
            🔖
            {chapterBookmarks > 0 && (
              <span className="Bookmark-count">{chapterBookmarks}</span>
            )}
          </button>

          <button
            className={`Highlights-nav-btn${showHighlights ? ' active' : ''}`}
            onClick={() => setShowHighlights(v => !v)}
            title="Highlights"
            aria-label="Highlights"
          >
            🖍
            {chapterHighlights > 0 && (
              <span className="Highlight-count">{chapterHighlights}</span>
            )}
          </button>

          <div className="Notes-io-control">
            <div className="Io-group">
              <span className="Io-group-label">{ui.ioLabelNotes}</span>
              <button className="Notes-io-btn" onClick={handleExportNotes} title={ui.exportNotes}>
                <svg viewBox="0 0 14 14" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="7" y1="1" x2="7" y2="9"/><polyline points="3.5,5.5 7,2 10.5,5.5"/><line x1="2" y1="12" x2="12" y2="12"/>
                </svg>
              </button>
              <button className="Notes-io-btn" onClick={() => importInputRef.current?.click()} title={ui.importNotes}>
                <svg viewBox="0 0 14 14" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="7" y1="1" x2="7" y2="9"/><polyline points="3.5,5.5 7,9 10.5,5.5"/><line x1="2" y1="12" x2="12" y2="12"/>
                </svg>
              </button>
              <input ref={importInputRef} type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={handleImportNotes} />
            </div>
            <div className="Io-group">
              <span className="Io-group-label">{ui.ioLabelHighlights}</span>
              <button className="Notes-io-btn" onClick={handleExportHighlights} title={ui.exportHighlights}>
                <svg viewBox="0 0 14 14" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="7" y1="1" x2="7" y2="9"/><polyline points="3.5,5.5 7,2 10.5,5.5"/><line x1="2" y1="12" x2="12" y2="12"/>
                </svg>
              </button>
              <button className="Notes-io-btn" onClick={() => highlightImportInputRef.current?.click()} title={ui.importHighlights}>
                <svg viewBox="0 0 14 14" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="7" y1="1" x2="7" y2="9"/><polyline points="3.5,5.5 7,9 10.5,5.5"/><line x1="2" y1="12" x2="12" y2="12"/>
                </svg>
              </button>
              <input ref={highlightImportInputRef} type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={handleImportHighlights} />
            </div>
          </div>
        </div>
      </div>

      <div className="Bible-reader-area" style={{ '--reader-fs': `${fontSize}px`, '--reader-ff': fontStack }}>
        {commentaryLoading && <div className="Commentary-loading">Loading {(() => { const c = COMMENTARIES.find(c => c.id === commentarySlug); return c?.labels[selectedLanguage] || c?.labels[selectedLanguage.split('-')[0]] || 'commentary'; })()}...</div>}
        {currentChapterVerses.map((verseText, index) => {
          const verseNumber = index + 1;
          const verseId = makeVerseKey(currentBook.name, chapterIndex + 1, verseNumber);
          const note = notes[verseId];
          const isEditing = activeVerse === verseId;
          const isNoteExpanded = expandedNote === verseId;
          const isCommentaryExpanded = expandedCommentary === verseId;
          const verseCommentary = commentaryData?.[verseNumber];

          const isGreekHighlighted = highlightedVerse === verseNumber;
          const englishNames = Object.keys(HELLOAO_BOOK_CODES);
          const crossRefs = getCrossRefs(crossRefIndex, englishNames[bookIndex], chapterIndex + 1, verseNumber);
          const hasCrossRefs = crossRefs.length > 0;
          const isCrossRefExpanded = expandedCrossRef === verseId;
          const isSelected = selectedVerse === verseId;

          return (
            <div
              key={index}
              ref={isGreekHighlighted ? highlightedVerseRef : null}
              className={`Verse-block${isEditing ? ' is-editing' : isSelected ? ' is-selected' : ''}${isGreekHighlighted ? ' greek-highlighted' : ''}`}
              data-verse-id={verseId}
              onMouseUp={(e) => handleVerseMouseUp(e, verseNumber)}
              onClick={(e) => {
                if (e.target.closest('button')) return;
                setSelectedVerse(prev => prev === verseId ? null : verseId);
              }}
            >
              <span className="Verse-num">{verseNumber}</span>
              {isGreekHighlighted ? highlightWords(verseText, highlightedGloss) : renderVerseText(verseText, verseId)}

              {(
                <button
                  className={`Note-indicator${isEditing ? ' active' : ''}${note?.content ? ' filled' : ''}`}
                  onClick={(e) => handleNoteIconClick(e, verseNumber)}
                  title={isEditing ? 'Close note' : note?.content ? 'Edit note' : 'Add note'}
                  aria-label="Toggle note editor"
                >
                  <svg viewBox="0 0 14 14" width="13" height="13"><path d="M9 1.5 L12.5 5 L4.5 13 L1 13 L1 9.5 Z M7.5 3 L11 6.5"/></svg>
                </button>
              )}

              {verseCommentary && !isEditing && (
                <button
                  className={`Commentary-indicator${isCommentaryExpanded ? ' active filled' : ''}`}
                  onClick={(e) => handleCommentaryIconClick(e, verseNumber)}
                  title={isCommentaryExpanded ? 'Hide commentary' : 'View commentary'}
                  aria-label="Toggle commentary"
                >
                  <svg viewBox="0 0 14 14" width="13" height="13"><path d="M1 2 C1 2 3.5 1 7 1 C10.5 1 13 2 13 2 L13 13 C13 13 10.5 12 7 12 C3.5 12 1 13 1 13 Z M7 1 L7 12"/></svg>
                </button>
              )}

              {hasCrossRefs && !isEditing && (
                <button
                  className={`CrossRef-indicator${isCrossRefExpanded ? ' active' : ''}`}
                  onClick={(e) => handleCrossRefIconClick(e, verseNumber)}
                  title={isCrossRefExpanded ? 'Hide cross references' : `${crossRefs.length} cross reference${crossRefs.length !== 1 ? 's' : ''}`}
                  aria-label="Toggle cross references"
                >
                  <svg viewBox="0 0 14 14" width="13" height="13"><path d="M1 5 L8 5 M6 3 L8 5 L6 7 M13 9 L6 9 M8 7 L6 9 L8 11"/></svg>
                </button>
              )}

              <button
                className={`Bookmark-indicator${bookmarks[verseId] ? ' active filled' : ''}`}
                onClick={(e) => handleBookmarkClick(e, verseNumber)}
                title={bookmarks[verseId] ? 'Remove bookmark' : 'Bookmark this verse'}
                aria-label={bookmarks[verseId] ? 'Remove bookmark' : 'Bookmark this verse'}
              >
                <svg viewBox="0 0 14 14" width="13" height="13"><path d="M3 1 L11 1 L11 13 L7 9.5 L3 13 Z"/></svg>
              </button>

              <button
                className={`AskAI-verse-btn${fetchingAiFor === verseNumber ? ' loading' : ''}${focusedVerseContext?.verse === verseNumber ? ' filled' : ''}`}
                onClick={(e) => handleAskAIForVerse(e, verseNumber)}
                title={`Ask AI about ${currentBook.name} ${chapterIndex + 1}:${verseNumber} (all 4 commentaries)`}
                aria-label={`Ask AI about verse ${verseNumber}`}
                disabled={fetchingAiFor !== null}
              >
                {fetchingAiFor === verseNumber ? '…' : (
                  <svg viewBox="0 0 14 14" width="13" height="13">
                    <path d="M7 1 L8.2 5.3 L12.5 7 L8.2 8.7 L7 13 L5.8 8.7 L1.5 7 L5.8 5.3 Z"/>
                  </svg>
                )}
              </button>

              {isEditing && (
                <div className="Note-editor" onClick={e => e.stopPropagation()}>
                  <textarea
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    placeholder={ui.notePlaceholder}
                    autoFocus
                  />
                  <div className="Note-actions">
                    <button className="Note-btn save" onClick={() => saveNote(verseNumber, isSubscribed ? 'remote' : 'local')}>{ui.save}</button>
                    <button className="Note-btn cancel" onClick={() => setActiveVerse(null)}>{ui.cancel}</button>
                  </div>
                </div>
              )}


              {verseCommentary && !isEditing && isCommentaryExpanded && (
                <div className="Verse-commentary" onClick={e => e.stopPropagation()}>
                  <div className="Commentary-body">
                    <div className="Commentary-content" dangerouslySetInnerHTML={{ __html: formatCommentary(verseCommentary) }} />
                  </div>
                </div>
              )}

              {hasCrossRefs && !isEditing && isCrossRefExpanded && (
                <div className="Verse-crossref" onClick={e => e.stopPropagation()}>
                  <div className="CrossRef-header">{ui.crossReferences ?? 'Cross References'} ({crossRefs.length})</div>
                  <ul className="CrossRef-list">
                    {crossRefs.map(({ book, chapter, verse: v }, i) => {
                      const crossRefBookIdx = Object.keys(HELLOAO_BOOK_CODES).indexOf(book);
                      const crossRefBookData = crossRefBookIdx >= 0 ? bibleData[crossRefBookIdx] : null;
                      const text = crossRefBookData ? (crossRefBookData.chapters[chapter - 1]?.[v - 1] ?? null) : getVerseText(bibleData, book, chapter, v);
                      const localBook = localizeBookName(book, selectedLanguage);
                      return (
                        <li key={i} className="CrossRef-item">
                          <button
                            className="CrossRef-ref"
                            onClick={() => navigateToCrossRef(book, chapter)}
                            title={`${localBook} ${chapter}`}
                          >
                            {localBook} {chapter}:{v}
                          </button>
                          {text && <span className="CrossRef-text">{text}</span>}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!showChat && !showHebrew && <AdBanner />}

      <div className="Bottom-nav">
        <button
          className="Bottom-nav-btn prev"
          onClick={goToPrevChapter}
          disabled={bookIndex === 0 && chapterIndex === 0}
          aria-label="Previous Chapter"
        >
          <svg viewBox="0 0 72 72" width="20" height="20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M40.016,59.212L20.791,39.984c-2.2-2.2-2.2-5.767,0-7.967l19.225-19.229c2.2-2.201,5.768-2.201,7.968,0l3.224,3.224
              c2.2,2.2,2.2,5.768,0,7.968L39.188,36l12.02,12.02c2.2,2.2,2.2,5.768,0,7.968l-3.224,3.224
              C45.784,61.413,42.217,61.413,40.016,59.212z"/>
          </svg>
          {ui.prevChapter}
        </button>
        <button
          className="Bottom-nav-btn next"
          onClick={goToNextChapter}
          disabled={bookIndex === bibleData.length - 1 && chapterIndex === currentBook.chapters.length - 1}
          aria-label="Next Chapter"
        >
          {ui.nextChapter}
          <svg viewBox="0 0 64 64" width="20" height="20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M21.364,52.6l-2.861-2.861c-1.953-1.953-1.953-5.118,0-7.071L29.17,32L18.502,21.332c-1.953-1.953-1.953-5.118,0-7.071
              l2.861-2.861c1.953-1.953,5.119-1.953,7.071,0l17.062,17.064c1.952,1.953,1.952,5.118,0,7.07L28.435,52.6
              C26.482,54.553,23.316,54.553,21.364,52.6z"/>
          </svg>
        </button>
      </div>
      </div>{/* end Bible-text-column */}

      {showChat && (
        <ChatPanel
          bibleData={bibleData}
          ragIndex={ragIndex}
          bookIdx={bookIndex}
          chapterIdx={chapterIndex}
          focusedVerse={focusedVerseContext}
          onClose={() => setShowChat(false)}
          language={selectedLanguage}
          user={user}
          isSubscribed={isSubscribed}
        />
      )}

      {showGreek && (
        <GreekPanel
          chapterData={greekChapterData}
          bookName={currentBook.name}
          chapterNum={chapterIndex + 1}
          onClose={() => { setShowGreek(false); setHighlightedVerse(null); setHighlightedGloss(null); }}
          onVerseHighlight={(verseNum, terms) => { setHighlightedVerse(verseNum); setHighlightedGloss(terms); }}
        />
      )}

      {showHebrew && (
        <HebrewPanel
          chapterData={hebrewChapterData}
          bookName={currentBook.name}
          chapterNum={chapterIndex + 1}
          onClose={() => { setShowHebrew(false); setHighlightedVerse(null); setHighlightedGloss(null); }}
          onVerseHighlight={(verseNum, terms) => { setHighlightedVerse(verseNum); setHighlightedGloss(terms); }}
        />
      )}

      {!showChat && !showGreek && !showHebrew && (
        <button
          className="Ask-ai-btn"
          onClick={() => { setFocusedVerseContext(null); setShowChat(true); }}
          aria-label="Open Bible AI chat"
          title="Ask Bible AI"
        >
          ✦ Ask AI
        </button>
      )}

      {activeHighlight && (
        <div
          className="Highlight-remove-toolbar"
          style={{
            position: 'fixed',
            left: `${activeHighlight.x}px`,
            top: `${activeHighlight.y}px`,
            transform: 'translate(-50%, calc(-100% - 8px))',
            zIndex: 1000,
          }}
        >
          <button
            className="Highlight-remove-action-btn"
            onClick={() => { removeHighlight(activeHighlight.verseId, activeHighlight.hlId); setActiveHighlight(null); }}
            title="Remove highlight"
            aria-label="Remove highlight"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6M14 11v6"/>
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
            </svg>
            Remove
          </button>
        </div>
      )}

      {highlightToolbar && (
        <div
          className="Highlight-toolbar"
          style={{
            position: 'fixed',
            left: `${highlightToolbar.x}px`,
            top: `${highlightToolbar.y}px`,
            transform: 'translate(-50%, calc(-100% - 8px))',
            zIndex: 1000,
          }}
        >
          {HIGHLIGHT_COLORS.map(({ id, color, swatch, label }) => (
            <button
              key={id}
              className="Highlight-color-btn"
              style={{ backgroundColor: swatch }}
              onClick={() => applyHighlight(color)}
              title={label}
              aria-label={`Highlight in ${label}`}
            />
          ))}
          <button
            className="Highlight-cancel-btn"
            onClick={() => { setHighlightToolbar(null); window.getSelection()?.removeAllRanges(); }}
            title="Cancel"
            aria-label="Cancel highlight"
          >✕</button>
        </div>
      )}
    </div>
  );
}

export default BibleContent;
